import React, { createContext, useContext, ReactNode } from 'react';
import useApi from 'hooks/useApi';

// Define the permissions enum based on your requirements
export const PERMISSIONS = {
  ADD_EDIT_PROCESS: 'Add/edit a process',
  WORKFLOW_EDIT: 'Workflow Edit',
  ADD_EDIT_NOTIFICATIONS: 'Add/edit Notifications',
  ADD_EDIT_HIDE_UNHIDE_FIELDS: 'Add/edit hide-unhide Fields',
  // Authoring AI detection rules on a process — creating, editing, deleting,
  // drafting, testing and publishing. Viewing the configured rules is not gated.
  ADD_EDIT_AI_DETECTION: 'Add/edit AI Detection',
  // Managing the on-device detection classes (what the mobile camera can
  // recognise). Uploading one changes behaviour on every installed app at once,
  // so unlike most permissions here this is ALSO enforced by the API.
  ADD_EDIT_OBJECT_DETECTION: 'Add/edit Object Detection',
  EDIT_TRANSACTION: 'Add/edit transaction',
  EXPORT_TRANSACTION: 'Export Transaction',
  BULK_IMPORT_TRANSACTIONS: 'Bulk import Transactions',
  DELETE_TRANSACTIONS: 'Delete transactions',
  ADD_EDIT_RESOURCE_TYPE: 'Add/edit a ResourceType',
  ADD_EDIT_RESOURCE: 'Add/edit a Resource',
  BULK_UPLOAD_RESOURCE: 'Bulk upload Resources',
  DASHBOARD_PAGE_ACCESS: 'Dashboard Page Access',
  ANALYTICS_PAGE_ACCESS: 'Analytics page access',
  ADD_EDIT_CLIENT: 'Add/edit a Client',
  ADD_EDIT_INTEGRATION_CONFIG: 'Add/edit Integration Config',
  CLIENT_PAGE_ACCESS: 'Client page access',
  CALLS_PAGE_ACCESS: 'Calls page access',
  TRANSACTION_EMBED_ACCESS: 'Transaction embed access',
  AI_SUPPORT_PAGE_ACCESS: 'AI Support page access',
  HELP_PAGE_ACCESS: 'Help page access'
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

interface PermissionContextType {
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  getUserPermissions: () => string[];
}

const PermissionContext = createContext<PermissionContextType | null>(null);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { user } = useApi();

  const getUserPermissions = (): string[] => {
    // Extract permissions from user.role.permissions array
    
    if (!user?.role?.permissions) {
      return [];
    }
    
    // Handle both array of strings and array of objects with permission names
    if (Array.isArray(user.role.permissions)) {
      return user.role.permissions.map((perm: any) => 
        typeof perm === 'string' ? perm : perm.name || perm.value || perm
      );
    }
    
    return [];
  };

  const hasPermission = (permission: Permission): boolean => {
    const userPermissions = getUserPermissions();
    return userPermissions.includes(permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    console.log("permissions data",permissions);
    return permissions.every(permission => hasPermission(permission));
  };

  const value: PermissionContextType = {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getUserPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Hook for checking if user has a specific permission
export const useHasPermission = (permission?: Permission): boolean => {
  const { hasPermission } = usePermissions();
  return permission ? hasPermission(permission) : true;
};

// Hook for checking if user has any of the provided permissions
export const useHasAnyPermission = (permissions: Permission[]): boolean => {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissions);
};

// Hook for checking if user has all of the provided permissions
export const useHasAllPermissions = (permissions: Permission[]): boolean => {
  const { hasAllPermissions } = usePermissions();
  return hasAllPermissions(permissions);
};
